import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { CreateProjectData, Project } from '../../../api/projectApi';
import { CreateTaskData } from '../../../api/taskApi';
import { useUsers } from '../../../hooks/useUsers';
import ProjectForm from '../../Forms/ProjectForm';

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (projectData: CreateProjectData, tasks: Partial<CreateTaskData>[]) => void;
  isLoading: boolean;
  editingProject: Project | null;
  formData: CreateProjectData;
  setFormData: (data: CreateProjectData) => void;
  projectTypes: readonly string[];
  clients: any[];
  projectTasks?: Partial<CreateTaskData>[]; // New prop for full project tasks
}

const ProjectModal: React.FC<ProjectModalProps> = ({
  isOpen,
  onClose,
  onSave,
  isLoading,
  editingProject,
  formData,
  setFormData,
  projectTypes,
  clients,
  projectTasks: projectTasksProp = [],
}) => {
  // Local state for tasks being created with the project
  const [projectTasks, setProjectTasks] = React.useState<Partial<CreateTaskData>[]>([]);
  const [showTaskCreation, setShowTaskCreation] = React.useState(false);
  const { data: users = [] } = useUsers();

  // Handlers for task creation
  const addTaskToProject = () => {
    setProjectTasks([
      ...projectTasks,
      {
        priority: 'medium',
        description: '',
        type: 'development',
        status: 'In progress',
        authBy: [],
        quotedPrice: undefined,
        dateStarted: undefined,
        dueDate: undefined,
      },
    ]);
  };
  const removeTaskFromProject = (index: number) => {
    setProjectTasks(projectTasks.filter((_, i) => i !== index));
  };
  const updateProjectTask = (index: number, taskData: Partial<CreateTaskData>) => {
    const updatedTasks = [...projectTasks];
    updatedTasks[index] = { ...updatedTasks[index], ...taskData };
    setProjectTasks(updatedTasks);
  };
  const formatDateForInput = (date: Date | string | undefined | null): string => {
    if (!date) return '';
    if (typeof date === 'string') return date;
    if (!(date instanceof Date) || isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };
  // When opening for editing, initialize projectTasks from editingProject's tasks
  const prevIsOpen = React.useRef<boolean>(false);
  React.useEffect(() => {
    if (isOpen && !prevIsOpen.current) {
      // Modal just opened
      if (editingProject && Array.isArray(projectTasksProp) && projectTasksProp.length > 0) {
        const normalizedTasks = projectTasksProp.map((t: any) => ({
          ...t,
          priority: t.priority || 'medium',
          description: t.description || '',
          type: t.type || 'development',
          status: t.status || 'In progress',
          authBy: Array.isArray(t.authBy)
            ? t.authBy.map((u: any) =>
                typeof u === 'string'
                  ? u
                  : u._id || u.$oid || (u && u.toString && u.toString()) || ''
              )
            : [],
          quotedPrice: t.quotedPrice,
          dateStarted: t.dateStarted,
          dueDate: t.dueDate,
        }));
        setProjectTasks(normalizedTasks);
      } else {
        setProjectTasks([]);
      }
      setShowTaskCreation(true);
    } else if (!isOpen && prevIsOpen.current) {
      // Modal just closed
      setProjectTasks([]);
      setShowTaskCreation(false);
    }
    prevIsOpen.current = isOpen;
  }, [isOpen, editingProject, projectTasksProp]);

  // Save handler
  const handleSave = () => {
    onSave(formData, projectTasks);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxH="90vh" overflowY="auto" py={6}>
        <ModalHeader>{editingProject ? 'Edit Project' : 'Create New Project'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <ProjectForm
            formData={formData}
            setFormData={setFormData}
            projectTypes={projectTypes}
            clients={clients}
            users={users}
            projectTasks={projectTasks}
            setProjectTasks={setProjectTasks}
            showTaskCreation={showTaskCreation}
            setShowTaskCreation={setShowTaskCreation}
            editingProject={editingProject}
            addTaskToProject={addTaskToProject}
            removeTaskFromProject={removeTaskFromProject}
            updateProjectTask={updateProjectTask}
            formatDateForInput={formatDateForInput}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="green" onClick={handleSave} isLoading={isLoading}>
            {editingProject ? 'Update' : 'Create Project'}
            {!editingProject && projectTasks.length > 0 && ` & ${projectTasks.length} Task(s)`}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ProjectModal;
